import { t as coreT } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Formik, FormikHelpers } from 'formik';
import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useState } from 'react';
import { usePreventNavigation } from 'shared/hooks';
import { useImageUpscaleFieldOption } from 'shared/hooks/use-image-upscale-access';

import { UDFormDropzone } from 'ud-form/components/image-dropzone';
import UDFormInput from 'ud-form/components/input';
import UDButton from 'ud-ui/components/button';
import * as yup from 'yup';
import { AnnouncedTeam } from 'modules/teams/domain/interfaces/announced-team';
import { Team } from 'modules/teams/domain/interfaces/team';

type FormValues = {
  name: string;
  shortName?: string;
  logo?: (File | string)[];
};

/** Возвращает, сохранилось ли: на провале форма снова считает правки несохранёнными. */
export type SubmitTeamForm = ({
  values,
  formikHelpers,
}: {
  values: FormValues;
  formikHelpers: FormikHelpers<FormValues>;
}) => Promise<boolean>;

type EditTeamFormProps = {
  onSubmit: SubmitTeamForm;
  initialValues?: Team | AnnouncedTeam;
  isLoading?: boolean;
};

const validateScheme = yup.object().shape({
  name: yup.string().required(() => coreT`Название команды обязательно`),
  shortName: yup
    .string()
    .nullable()
    .optional()
    .max(255, () => coreT`Максимальная длина короткого названия 255 символов`),
  logo: yup.mixed().nullable().optional(),
});

const TeamForm = (props: EditTeamFormProps) => {
  const { t } = useLingui();
  const logoUpscale = useImageUpscaleFieldOption('graphic');
  const { initialValues: initialTeam, onSubmit, isLoading } = props;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.TEAMS.INDEX,
  });

  const initialValues: FormValues = {
    name: initialTeam?.name ?? '',
    shortName: initialTeam?.shortName ?? '',
    logo: initialTeam?.logo ? [initialTeam.logo.url] : [],
  };

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validationSchema={validateScheme}
      onSubmit={async (values, formikHelpers) => {
        // На время запроса текущие значения — база: успех уводит со страницы, и защита от ухода
        // (снимается эффектом после перерисовки) не должна встретить его окном «Покинуть?».
        formikHelpers.resetForm({ values });
        setIsFormDirty(false);

        const saved = await onSubmit({ values, formikHelpers });
        if (!saved) {
          // Не сохранилось — правки снова несохранённые: «Сохранить» доступна, уход спросит подтверждения.
          formikHelpers.resetForm({ values: initialValues });
          formikHelpers.setValues(values, false);
          setIsFormDirty(true);
        }
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {(formProps) => {
        return (
          <form className="flex flex-col" onSubmit={formProps.handleSubmit}>
            <div className="grid grid-cols-12 items-start gap-x-12">
              <div className={'col-span-6 flex flex-col'}>
                <UDFormInput name="name" label={t`Название команды`} placeholder={t`Введите название`} />
                <UDFormInput
                  name="shortName"
                  label={t`Короткое название (аббревиатура)`}
                  placeholder={t`Введите короткое название (напр. MUN)`}
                  containerProps={{ className: 'mt-4' }}
                  maxLength={255}
                />
              </div>
              <div className="col-span-6">
                <UDFormDropzone name="logo" label={t`Логотип команды`} crop removable upscale={logoUpscale} className="p-6" />
              </div>
            </div>

            <div className="mt-4 mb-2 flex w-1/2 justify-start pr-6">
              <UDButton
                type="submit"
                variant="primary"
                className="mr-4 w-1/2"
                disabled={isLoading || !formProps.dirty}
                loading={isLoading || formProps.isSubmitting}
              >
                <Trans>Сохранить</Trans>
              </UDButton>

              <UDButton
                type="button"
                variant="secondary"
                disabled={isLoading || formProps.isSubmitting}
                onClick={backHandler}
                className="w-1/2"
              >
                <Trans>Отменить</Trans>
              </UDButton>
            </div>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};

export default TeamForm;
